import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ArrowRightLeft,
  AlertTriangle,
  Stethoscope,
  CheckCircle2,
  ArrowRight,
  RefreshCw,
  Clock,
} from 'lucide-react';
import type {
  OrganizacaoColaboradorItemContract,
  CargoRiscosPorGrupoContract,
  CargoDetalhesCompletosContract,
  EfetivarMudancaFuncaoResponseContract,
} from '@sstexames/shared-types';
import { cargosService, type Cargo } from '../../services/cargos.service';
import { organizacaoService } from '../../services/organizacao.service';
import { formatCPF } from '../../utils/formatters';
import { useToast, ToastContainer } from '../Toast';

interface ModalMudancaFuncaoProps {
  isOpen: boolean;
  onClose: () => void;
  colaborador: OrganizacaoColaboradorItemContract | null;
  setoresList: Array<{ id: number; nome: string }>;
  empresaId?: number;
  onSuccess: (colaboradorId?: number) => void;
}

export function ModalMudancaFuncao({
  isOpen,
  onClose,
  colaborador,
  setoresList,
  empresaId,
  onSuccess,
}: ModalMudancaFuncaoProps) {
  const navigate = useNavigate();
  const toast = useToast();

  const [novoSetorId, setNovoSetorId] = useState<number | ''>('');
  const [novoCargoId, setNovoCargoId] = useState<number | ''>('');
  const [cargosSetor, setCargosSetor] = useState<Array<{ id: number; nome: string }>>([]);
  const [loadingCargos, setLoadingCargos] = useState(false);

  // Estados de análise de riscos e aptidões
  const [loadingAnalise, setLoadingAnalise] = useState(false);
  const [dadosCargoAtual, setDadosCargoAtual] = useState<CargoDetalhesCompletosContract | null>(null);
  const [dadosCargoNovo, setDadosCargoNovo] = useState<CargoDetalhesCompletosContract | null>(null);

  // Formulário de confirmação quando dispensado de exame
  const [dataInicioFuncao, setDataInicioFuncao] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sucessoData, setSucessoData] = useState<EfetivarMudancaFuncaoResponseContract | null>(null);

  const targetEmpresaId = empresaId || (colaborador as { empresa_id?: number } | null)?.empresa_id;

  // Carregar dados do cargo atual do colaborador ao abrir ou mudar
  useEffect(() => {
    if (!isOpen || !colaborador || !colaborador.cargo_id) {
      setDadosCargoAtual(null);
      setNovoSetorId('');
      setNovoCargoId('');
      setCargosSetor([]);
      setDadosCargoNovo(null);
      return;
    }

    cargosService
      .getDetalhesCargo(
        colaborador.cargo_id,
        targetEmpresaId ? Number(targetEmpresaId) : undefined,
        colaborador.setor_id ? Number(colaborador.setor_id) : undefined
      )
      .then((res) => {
        setDadosCargoAtual(res);
      })
      .catch((err) => {
        console.warn('Erro ao carregar dados do cargo atual:', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, colaborador?.id, colaborador?.cargo_id, colaborador?.setor_id, targetEmpresaId]);

  // Carregar cargos vinculados ao novo setor selecionado
  // Regra de negócio: não exibir o cargo que o próprio colaborador já ocupa
  useEffect(() => {
    setNovoCargoId('');
    setDadosCargoNovo(null);

    if (!novoSetorId) {
      setCargosSetor([]);
      return;
    }

    setLoadingCargos(true);
    cargosService
      .findAll({
        setor_id: Number(novoSetorId),
        empresa_id: targetEmpresaId ? Number(targetEmpresaId) : undefined,
        limit: 1000,
      })
      .then((res) => {
        const isMesmoSetor = Number(novoSetorId) === Number(colaborador?.setor_id);
        const cargos = (res.data || [])
          .filter((c: Cargo) => {
            // Só exclui o cargo atual se o colaborador estiver permanecendo no MESMO setor
            if (isMesmoSetor && Number(c.id) === Number(colaborador?.cargo_id)) {
              return false;
            }
            return true;
          })
          .map((c: Cargo) => ({ id: c.id, nome: c.nome }));
        setCargosSetor(cargos);
      })
      .catch((err) => {
        console.warn('Erro ao carregar cargos do setor:', err);
        setCargosSetor([]);
      })
      .finally(() => {
        setLoadingCargos(false);
      });
  }, [novoSetorId, colaborador?.cargo_id, colaborador?.setor_id, targetEmpresaId]);

  // Carregar exames, riscos e aptidões do novo cargo selecionado
  useEffect(() => {
    if (!novoCargoId || !novoSetorId) {
      setDadosCargoNovo(null);
      return;
    }

    setLoadingAnalise(true);
    cargosService
      .getDetalhesCargo(Number(novoCargoId), targetEmpresaId ? Number(targetEmpresaId) : undefined, Number(novoSetorId))
      .then((res) => {
        setDadosCargoNovo(res);
      })
      .catch((err) => {
        console.warn('Erro ao carregar detalhes do novo cargo:', err);
        toast.error('Não foi possível carregar os riscos e exames do cargo selecionado.');
      })
      .finally(() => {
        setLoadingAnalise(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [novoCargoId, novoSetorId, targetEmpresaId]);

  // Comparação de complexidade: aptidões especiais, novos riscos ocupacionais e exames complementares
  const analiseResultado = useMemo(() => {
    if (!dadosCargoNovo) return null;

    const normalize = (str?: string | null) =>
      (str || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const isTextoIgnoravel = (str?: string | null) => {
      if (!str) return true;
      const s = normalize(str);
      return (
        s.startsWith('atividades gerais') ||
        s.startsWith('atividades da funcao') ||
        s.startsWith('atividades da função') ||
        s.startsWith('atividades:') ||
        s.startsWith('ausencia') ||
        s.startsWith('nenhuma') ||
        s === 'nenhum'
      );
    };

    // 1. Aptidões Especiais (ex: Trabalho em Altura, Espaço Confinado, Eletricidade, etc.)
    const aptidoesAtual = (dadosCargoAtual?.aptidoes || [])
      .filter((a) => !isTextoIgnoravel(a))
      .map(normalize);
    const aptidoesNovoRaw = (dadosCargoNovo.aptidoes || []).filter(
      (a) => !isTextoIgnoravel(a),
    );
    const novasAptidoes = aptidoesNovoRaw.filter(
      (apt) => !aptidoesAtual.includes(normalize(apt)),
    );
    const hasNovasAptidoes = novasAptidoes.length > 0;

    // 2. Riscos Ocupacionais Reais por Grupo (Físico, Químico, Biológico, Ergonômico, Acidente)
    const extrairAgentes = (
      riscosPorGrupo?:
        | CargoRiscosPorGrupoContract
        | Record<string, Array<{ id?: string; agente?: string; nome?: string; descricao?: string }>>
        | null,
    ): string[] => {
      if (!riscosPorGrupo || typeof riscosPorGrupo !== 'object') return [];
      const agentes: string[] = [];
      const grupos = Object.values(riscosPorGrupo) as Array<
        Array<{ id?: string; agente?: string; nome?: string; descricao?: string }>
      >;
      for (const itens of grupos) {
        if (Array.isArray(itens)) {
          for (const item of itens) {
            const nomeAgente = item?.agente || item?.nome || item?.descricao;
            if (nomeAgente && !isTextoIgnoravel(nomeAgente)) {
              agentes.push(normalize(nomeAgente));
            }
          }
        }
      }
      return Array.from(new Set(agentes));
    };

    const agentesAtual = extrairAgentes(dadosCargoAtual?.riscos_por_grupo);
    const agentesNovoRaw: string[] = [];
    if (dadosCargoNovo.riscos_por_grupo && typeof dadosCargoNovo.riscos_por_grupo === 'object') {
      const grupos = Object.values(dadosCargoNovo.riscos_por_grupo) as Array<
        Array<{ id?: string; agente?: string; nome?: string; descricao?: string }>
      >;
      for (const itens of grupos) {
        if (Array.isArray(itens)) {
          for (const item of itens) {
            const nomeAgente = item?.agente || item?.nome || item?.descricao;
            if (nomeAgente && !isTextoIgnoravel(nomeAgente)) {
              agentesNovoRaw.push(nomeAgente.trim());
            }
          }
        }
      }
    }

    const novosRiscos = agentesNovoRaw.filter(
      (agente) => !agentesAtual.includes(normalize(agente)),
    );
    const hasNovosRiscos = novosRiscos.length > 0;

    // 3. Exames Complementares adicionais (desconsiderando a avaliação clínica básica comum a todos os cargos)
    const examesAtualNomes = (dadosCargoAtual?.exames || [])
      .map((e) => normalize(e.exame_nome))
      .filter((n) => n && !n.includes('clinica'));

    const examesNovoRaw = (dadosCargoNovo.exames || []).filter((e) => {
      const n = normalize(e.exame_nome);
      return n && !n.includes('clinica');
    });

    const novosExames = examesNovoRaw.filter(
      (e) => !examesAtualNomes.includes(normalize(e.exame_nome)),
    );
    const hasNovosExames = novosExames.length > 0;

    // Critério atualizado: apenas Novas Aptidões Especiais ou Novos Exames Complementares tornam o exame obrigatório.
    // Riscos gerais (ex: ergonômico ou acidentes simples como queda de mesmo nível) continuam sendo exibidos visualmente na tela, mas não exigem ASO de Mudança de Função se não houver exames complementares ou aptidões adicionais.
    const necessitaExame = hasNovasAptidoes || hasNovosExames;

    return {
      necessitaExame,
      hasNovasAptidoes,
      novasAptidoes,
      hasNovosRiscos,
      novosRiscos,
      hasNovosExames,
      novosExames,
      totalRiscosNovo: agentesNovoRaw.length,
      totalRiscosAtual: agentesAtual.length,
    };
  }, [dadosCargoAtual, dadosCargoNovo]);

  if (!isOpen || !colaborador) return null;

  // Ação A: Quando NÃO necessita de exame, efetivar atualização direta com data de início
  const handleConfirmarSemExame = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!novoSetorId || !novoCargoId) {
      toast.error('Selecione o setor e o novo cargo.');
      return;
    }

    if (!dataInicioFuncao) {
      toast.error('Informe a data de início na nova função.');
      return;
    }

    const setorSelecionado = setoresList.find((s) => s.id === Number(novoSetorId));
    const cargoSelecionado = cargosSetor.find((c) => c.id === Number(novoCargoId));
    const setorNomeDestino = setorSelecionado?.nome || '';
    const cargoNomeDestino = cargoSelecionado?.nome || dadosCargoNovo?.nome || '';

    setIsSubmitting(true);
    try {
      const res = await organizacaoService.efetivarMudancaFuncao({
        empresa_id: empresaId,
        colaborador_id: colaborador.id,
        novo_setor_id: Number(novoSetorId),
        novo_cargo_id: Number(novoCargoId),
        data_inicio: dataInicioFuncao,
      });

      setSucessoData({
        ...res,
        novo_setor_nome: res?.novo_setor_nome || setorNomeDestino,
        novo_cargo_nome: res?.novo_cargo_nome || cargoNomeDestino,
        cargo_anterior_nome: res?.cargo_anterior_nome || colaborador.cargo_nome,
        novo_setor_id: Number(novoSetorId),
        novo_cargo_id: Number(novoCargoId),
        data_inicio: res?.data_inicio || dataInicioFuncao,
      });
      toast.success('Função atualizada com sucesso no quadro de colaboradores!');
    } catch (err: unknown) {
      console.error('Erro ao atualizar função do colaborador:', err);
      const errObj = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = errObj?.response?.data?.message || errObj?.message || 'Falha ao atualizar função.';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao atualizar função.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ação B: Quando NECESSITA de exame, registrar intenção e redirecionar para o agendamento no wizard
  const handleAvancarParaAgendamento = async () => {
    if (!novoSetorId || !novoCargoId || !dadosCargoNovo) return;

    const setorSelecionado = setoresList.find((s) => s.id === Number(novoSetorId));
    const cargoSelecionado = cargosSetor.find((c) => c.id === Number(novoCargoId));

    const examesIds = Array.isArray(dadosCargoNovo.exames_clinicos_ids)
      ? dadosCargoNovo.exames_clinicos_ids
      : [];

    // Registrar intenção no backend de mudança de função dependente de exame
    try {
      await organizacaoService.iniciarMudancaFuncao({
        empresa_id: empresaId,
        colaborador_id: colaborador.id,
        novo_setor_id: Number(novoSetorId),
        novo_cargo_id: Number(novoCargoId),
      });
    } catch (auditErr) {
      console.warn('Aviso ao registrar intenção de mudança de função:', auditErr);
    }

    // Montar payload estruturado do colaborador com nova função e tipo de exame Mudança de Função (5)
    const mudancaEmployee = {
      id: String(colaborador.id),
      nome: colaborador.nome,
      nome_social: colaborador.nome_social || undefined,
      pcd: colaborador.pcd ?? undefined,
      enviar_esocial: colaborador.enviar_esocial ?? undefined,
      cpf: formatCPF(colaborador.cpf),
      cargo: cargoSelecionado?.nome || colaborador.cargo_nome,
      cargo_id: Number(novoCargoId),
      setor: setorSelecionado?.nome || colaborador.setor_nome,
      setor_id: Number(novoSetorId),
      dataAdmissao: colaborador.data_admissao
        ? colaborador.data_admissao.split('T')[0]
        : undefined,
      matricula_esocial: colaborador.matricula || undefined,
      is_candidato: false,
      is_existente: true,
      exame: '5', // Mudança de Riscos Ocupacionais / Mudança de Função
      escolha_clinica_tipo: 'rh',
      variacao: '1',
      examesSelecionados: examesIds,
      protocoloExames: examesIds,
      examesPontuais: [],
      examesSelecionadosCount: examesIds.length,
      modeloExame: '0',
    };

    try {
      localStorage.setItem('customer_employeesData', JSON.stringify([mudancaEmployee]));
      if (empresaId) {
        localStorage.setItem('customer_employeesData_empresa_id', empresaId.toString());
      }
      localStorage.setItem('customer_scheduling_mode', 'lote_manual');

      toast.info('Redirecionando para o agendamento de Mudança de Função...');
      handleClose();
      navigate('/agendamento/cadastro?tab=lote_manual');
    } catch (err) {
      console.error('Erro ao configurar agendamento de mudança de função:', err);
      toast.error('Não foi possível iniciar o agendamento.');
    }
  };

  const handleClose = () => {
    const colabId = colaborador?.id;
    const wasSuccess = Boolean(sucessoData);
    setNovoSetorId('');
    setNovoCargoId('');
    setCargosSetor([]);
    setDadosCargoAtual(null);
    setDadosCargoNovo(null);
    setDataInicioFuncao(new Date().toISOString().split('T')[0]);
    setSucessoData(null);
    onClose();
    if (wasSuccess && colabId) {
      onSuccess(colabId);
    }
  };

  const modalElement = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/70">
          <div className="flex items-center space-x-3">
            <div className={`p-2.5 rounded-xl border ${
              sucessoData
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-primary-50 text-primary-700 border-primary-100'
            }`}>
              {sucessoData ? <CheckCircle2 className="w-5 h-5" /> : <ArrowRightLeft className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">
                {sucessoData ? 'Mudança de Função Concluída' : 'Mudar de Função'}
              </h3>
              <p className="text-xs text-gray-500 font-medium">
                Colaborador: <strong className="text-gray-900">{colaborador.nome}</strong> · CPF{' '}
                <span className="font-mono">{formatCPF(colaborador.cpf)}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {sucessoData ? (
          <div className="p-8 text-center space-y-6 overflow-y-auto">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner animate-in zoom-in-75 duration-300">
              <CheckCircle2 className="w-10 h-10" />
            </div>

            <div className="space-y-1.5">
              <h3 className="text-xl font-bold text-gray-900">
                Mudança de Função Efetivada com Sucesso!
              </h3>
              <p className="text-sm text-gray-600">
                O colaborador <strong className="text-gray-900">{colaborador.nome}</strong> foi atualizado para a nova posição no quadro de pessoas.
              </p>
            </div>

            {/* Card com resumo da transição */}
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl text-left space-y-3 max-w-lg mx-auto shadow-xs">
              <div className="grid grid-cols-2 gap-3.5">
                <div className="bg-white/80 border border-slate-200 rounded-xl p-3.5">
                  <span className="text-[10px] font-bold uppercase text-slate-400 block tracking-wider">
                    Posição Anterior
                  </span>
                  <p className="text-xs text-slate-500 mt-1">
                    Setor: <strong className="font-semibold text-slate-700">{colaborador.setor_nome || 'N/A'}</strong>
                  </p>
                  <p className="text-sm font-semibold text-slate-600 line-through decoration-slate-400 mt-0.5">
                    {sucessoData.cargo_anterior_nome || colaborador.cargo_nome}
                  </p>
                </div>
                <div className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-3.5">
                  <span className="text-[10px] font-bold uppercase text-emerald-700 block tracking-wider">
                    Nova Posição Efetivada ★
                  </span>
                  <p className="text-xs text-emerald-800 mt-1">
                    Setor: <strong className="font-bold text-emerald-950">{sucessoData.novo_setor_nome || 'N/A'}</strong>
                  </p>
                  <p className="text-sm font-bold text-emerald-950 mt-0.5">
                    {sucessoData.novo_cargo_nome || 'Novo Cargo'}
                  </p>
                </div>
              </div>

              <div className="pt-2.5 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
                <span>Data de Início: <strong>{sucessoData.data_inicio}</strong></span>
                <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Dispensa NR-7 Registrada
                </span>
              </div>
            </div>

            {/* Aviso da trava regulatória de 24h */}
            <div className="flex items-center justify-center space-x-2.5 text-xs text-amber-800 bg-amber-50/90 border border-amber-200 px-4 py-3 rounded-xl max-w-lg mx-auto text-left shadow-2xs">
              <Clock className="w-5 h-5 text-amber-600 shrink-0" />
              <span>
                Para garantir a integridade dos dados e conformidade de SST, o botão de mudança de função deste colaborador ficará inativo pelas próximas <strong>24 horas</strong>.
              </span>
            </div>

            <div className="pt-2 flex justify-center">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-md transition-all cursor-pointer hover:shadow-lg active:scale-95"
              >
                <span>Concluir e Ver no Quadro</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-5 overflow-y-auto">
          {/* Card Resumo da Função Atual */}
          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1.5">
            <span className="font-bold uppercase tracking-wider text-slate-500 text-[10px] block">
              Posição Atual
            </span>
            <div className="grid grid-cols-2 gap-3 text-slate-800">
              <div>
                <span className="text-slate-500 block text-[11px]">Setor Atual:</span>
                <strong className="text-sm">{colaborador.setor_nome || 'Não definido'}</strong>
              </div>
              <div>
                <span className="text-slate-500 block text-[11px]">Cargo Atual:</span>
                <strong className="text-sm">{colaborador.cargo_nome || 'Não definido'}</strong>
              </div>
            </div>
          </div>

          {/* Seletores: Novo Setor e Novo Cargo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Novo Setor Destino
                <span className="text-red-500 font-bold ml-0.5">*</span>
              </label>
              <select
                value={novoSetorId}
                onChange={(e) => setNovoSetorId(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow outline-none text-gray-800"
              >
                <option value="">Selecione o novo setor...</option>
                {setoresList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Novo Cargo Desejado
                <span className="text-red-500 font-bold ml-0.5">*</span>
              </label>
              <select
                disabled={!novoSetorId || loadingCargos}
                value={novoCargoId}
                onChange={(e) => setNovoCargoId(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow outline-none text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">
                  {loadingCargos
                    ? 'Carregando cargos do setor...'
                    : !novoSetorId
                    ? 'Primeiro selecione o setor...'
                    : cargosSetor.length === 0
                    ? 'Nenhum outro cargo disponível neste setor'
                    : 'Selecione o novo cargo...'}
                </option>
                {cargosSetor.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Loading da Análise de Riscos e Aptidões */}
          {loadingAnalise && (
            <div className="p-8 text-center bg-gray-50/70 border border-gray-200 rounded-xl">
              <RefreshCw className="w-6 h-6 text-primary-500 animate-spin mx-auto mb-2" />
              <p className="text-xs font-semibold text-gray-700">
                Avaliando riscos ocupacionais e aptidões da nova função...
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5">
                Comparando protocolo do PGR/PCMSO conforme exigências da NR-7
              </p>
            </div>
          )}

          {/* Resultado da Análise de Complexidade */}
          {!loadingAnalise && analiseResultado && dadosCargoNovo && (
            <div className="space-y-4 pt-1">
              {/* Banner de Decisão Ocupacional (NR-7) */}
              {analiseResultado.necessitaExame ? (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl space-y-1.5">
                  <div className="flex items-center space-x-2 text-amber-900 font-bold text-sm">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                    <span>Exame de Mudança de Função Obrigatório (NR-7)</span>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed pl-7">
                    O novo cargo apresenta{' '}
                    {analiseResultado.hasNovasAptidoes && <strong>novas aptidões especiais</strong>}
                    {analiseResultado.hasNovasAptidoes && analiseResultado.hasNovosExames && ' e '}
                    {analiseResultado.hasNovosExames && <strong>novos exames complementares do PCMSO</strong>}{' '}
                    não exigidos na função anterior. Conforme o item 7.5.9 da NR-7, é mandatório realizar a avaliação clínica e exames complementares antes da efetivação da mudança.
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-xl space-y-1.5">
                  <div className="flex items-center space-x-2 text-emerald-900 font-bold text-sm">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <span>Aprovado por Dispensa de Exame (NR-7)</span>
                  </div>
                  <p className="text-xs text-emerald-800 leading-relaxed pl-7">
                    O novo cargo <strong>não exige novos exames complementares nem novas aptidões especiais</strong>. Conforme a NR-7, a alteração de cargo pode ser realizada por dispensa de exame ocupacional, bastando registrar a data de início na nova função.
                  </p>
                </div>
              )}

              {/* Cards Comparativos: Cargo Anterior vs Novo Cargo */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                {/* Coluna 1: Cargo Anterior */}
                <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-xl space-y-2.5">
                  <div className="flex items-center justify-between border-b border-gray-200 pb-1.5">
                    <span className="font-bold text-gray-700 uppercase tracking-wider text-[10px]">
                      Cargo Anterior
                    </span>
                    <span className="text-[11px] font-semibold text-gray-500">
                      {colaborador.cargo_nome}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] font-semibold text-gray-400 block uppercase mb-1">
                      Aptidões Especiais Requeridas
                    </span>
                    {dadosCargoAtual?.aptidoes?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {dadosCargoAtual.aptidoes.map((a, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 rounded text-[11px] font-medium bg-white text-gray-700 border border-gray-200"
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic text-[11px]">Nenhuma aptidão especial requerida</span>
                    )}
                  </div>

                  <div>
                    <span className="text-[10px] font-semibold text-gray-400 block uppercase mb-1">
                      Riscos Ocupacionais
                    </span>
                    <span className="text-[11px] text-gray-600 font-medium">
                      {analiseResultado.totalRiscosAtual > 0
                        ? `${analiseResultado.totalRiscosAtual} agente(s) de risco mapeado(s)`
                        : 'Ausência de riscos ocupacionais'}
                    </span>
                  </div>

                  {dadosCargoAtual?.atividades_gerais && (
                    <div className="pt-2 border-t border-gray-200/60">
                      <span className="text-[10px] font-semibold text-gray-400 block uppercase mb-0.5">
                        Atividades Gerais da Função
                      </span>
                      <p className="text-[11px] text-gray-500 leading-snug line-clamp-2">
                        {dadosCargoAtual.atividades_gerais}
                      </p>
                    </div>
                  )}
                </div>

                {/* Coluna 2: Novo Cargo */}
                <div
                  className={`p-3.5 rounded-xl border space-y-2.5 ${
                    analiseResultado.necessitaExame
                      ? 'bg-amber-50/50 border-amber-200'
                      : 'bg-emerald-50/50 border-emerald-200'
                  }`}
                >
                  <div className="flex items-center justify-between border-b border-gray-200 pb-1.5">
                    <span
                      className={`font-bold uppercase tracking-wider text-[10px] ${
                        analiseResultado.necessitaExame ? 'text-amber-800' : 'text-emerald-800'
                      }`}
                    >
                      Novo Cargo
                    </span>
                    <span className="text-[11px] font-bold text-gray-900">
                      {dadosCargoNovo.nome}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] font-semibold text-gray-400 block uppercase mb-1">
                      Aptidões Especiais Requeridas
                    </span>
                    {dadosCargoNovo.aptidoes?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {dadosCargoNovo.aptidoes.map((a, i) => {
                          const isNova = analiseResultado.novasAptidoes.includes(a);
                          return (
                            <span
                              key={i}
                              className={`px-2 py-0.5 rounded text-[11px] font-medium border ${
                                isNova
                                  ? 'bg-amber-100 text-amber-900 border-amber-300 font-bold'
                                  : 'bg-white text-gray-700 border-gray-200'
                              }`}
                            >
                              {a} {isNova && '★ (Nova)'}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic text-[11px]">Nenhuma aptidão especial requerida</span>
                    )}
                  </div>

                  <div>
                    <span className="text-[10px] font-semibold text-gray-400 block uppercase mb-1">
                      Riscos Ocupacionais
                    </span>
                    <span className="text-[11px] text-gray-800 font-medium">
                      {analiseResultado.totalRiscosNovo > 0
                        ? `${analiseResultado.totalRiscosNovo} agente(s) de risco mapeado(s)`
                        : 'Ausência de riscos ocupacionais'}
                    </span>
                    {analiseResultado.novosRiscos.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {analiseResultado.novosRiscos.map((r, i) => (
                          <span
                            key={i}
                            className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-800 border border-rose-200"
                          >
                            + {r}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {dadosCargoNovo.atividades_gerais && (
                    <div className="pt-2 border-t border-gray-200/60">
                      <span className="text-[10px] font-semibold text-gray-400 block uppercase mb-0.5">
                        Atividades Gerais da Função
                      </span>
                      <p className="text-[11px] text-gray-600 leading-snug line-clamp-2">
                        {dadosCargoNovo.atividades_gerais}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Exames do Novo Cargo (quando necessita exame) */}
              {analiseResultado.necessitaExame && (
                <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-xl space-y-2 text-xs">
                  <div className="flex items-center space-x-2 text-gray-800 font-semibold">
                    <Stethoscope className="w-4 h-4 text-primary-600" />
                    <span>Exames que serão agendados para a Mudança de Função:</span>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 pl-1 text-[11px]">
                    <li>Avaliação Clínica Ocupacional (Mudança de Função)</li>
                    {dadosCargoNovo.exames?.map((ex, idx) => (
                      <li key={idx} className="font-medium text-gray-800">
                        {ex.exame_nome}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Data de início (quando NÃO necessita de exame) */}
              {!analiseResultado.necessitaExame && (
                <form onSubmit={handleConfirmarSemExame} className="pt-2 space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Data de Início na Nova Função
                      <span className="text-red-500 font-bold ml-0.5">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        required
                        value={dataInicioFuncao}
                        onChange={(e) => setDataInicioFuncao(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow outline-none text-gray-800 font-sans"
                      />
                    </div>
                    <span className="text-[11px] text-gray-500 mt-1 block">
                      A atualização será registrada sem a necessidade de emissão prévia de ASO de mudança de riscos.
                    </span>
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-xs transition-all cursor-pointer disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin text-white" />
                          <span>Atualizando...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Confirmar Mudança de Função</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
        )}

        {/* Footer para o caso de Exame Obrigatório */}
        {!sucessoData && analiseResultado?.necessitaExame && (
          <div className="p-4 bg-gray-50/90 border-t border-gray-200 flex items-center justify-between">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleAvancarParaAgendamento}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 rounded-xl shadow-md transition-all cursor-pointer"
            >
              <span>Avançar para Agendamento</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalElement, document.body) : modalElement;
}
